import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LanguageSwitcher } from '@/components/language-switcher';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { useLocale } from '@/i18n/locale-context';
import { cn } from '@/lib/utils';

const SECTION_IDS = ['workspace', 'capabilities', 'demos', 'faq', 'download'] as const;

function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function SiteHeader({ className }: { className?: string }) {
  const { t } = useLocale();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    function handleScroll() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        setScrollProgress(Math.min((scrollTop / docHeight) * 100, 100));
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let bestEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting && (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio)) {
            bestEntry = entry;
          }
        }
        if (bestEntry) {
          setActiveSection(bestEntry.target.id);
        }
      },
      { threshold: [0.1, 0.2, 0.3, 0.4, 0.5], rootMargin: '-80px 0px -20% 0px' }
    );

    sections.forEach((section) => observerRef.current!.observe(section));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    scrollToSection(e, id);
    setMobileMenuOpen(false);
  }, []);

  const navLinks = [
    { id: 'capabilities', label: t.navigation.capabilities },
    { id: 'demos', label: t.navigation.demos },
    { id: 'faq', label: t.navigation.faq },
    { id: 'download', label: t.navigation.download },
  ];

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 flex h-14 items-center bg-background px-6 sm:px-10',
        className
      )}
    >
      {/* 左侧 Logo */}
      <div className="flex shrink-0 basis-auto">
        <a
          href="/"
          className="inline-flex items-baseline gap-2.5 transition-opacity hover:opacity-80"
        >
          <span className="text-[15px] font-semibold tracking-tight text-foreground">
            Fustar
          </span>
        </a>
      </div>

      {/* 中间导航 */}
      <nav className="hidden flex-1 justify-center items-center gap-6 text-sm font-medium text-muted-foreground md:flex">
        {navLinks.map((link) => (
          <a
            key={link.id}
            className={cn(
              'nav-link-glow transition-colors hover:text-foreground',
              activeSection === link.id ? 'text-foreground font-medium' : 'text-muted-foreground'
            )}
            href={`#${link.id}`}
            aria-current={activeSection === link.id ? 'page' as const : undefined}
            onClick={(e) => scrollToSection(e, link.id)}
          >
            {link.label}
          </a>
        ))}
      </nav>

      {/* 右侧操作区 */}
      <div className="flex shrink-0 basis-auto items-center gap-3">
        <ThemeSwitcher />
        <LanguageSwitcher />
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground sm:hidden"
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
        >
          <Menu className="h-4 w-4" />
        </button>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-border/20">
        <div
          className="h-full bg-foreground/10 transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40 sm:hidden"
              onClick={() => setMobileMenuOpen(false)}
              onTouchEnd={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
              }}
              aria-hidden="true"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="fixed inset-y-0 right-0 z-50 w-64 border-l border-border/60 bg-background p-6 shadow-xl sm:hidden"
            >
              <div className="flex justify-end">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <nav className="mt-8 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a
                    key={link.id}
                    className={cn(
                      'text-sm transition-colors hover:text-foreground',
                      activeSection === link.id ? 'text-foreground font-medium' : 'text-muted-foreground'
                    )}
                    href={`#${link.id}`}
                    aria-current={activeSection === link.id ? 'page' as const : undefined}
                    onClick={(e) => handleNavClick(e, link.id)}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
